import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FiDownload, FiCalendar, FiHardDrive, FiPackage
} from 'react-icons/fi';
import { getCategoryIcon } from './icon/CategoryIcons';
import { useAuth } from '../context/AuthContext';
import DownloadHeader from './DownloadHeader';
import { getSoftwareCategories } from '../api/api';

const SoftwareDownloadPage = () => {
  const { tokens } = useAuth();
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedVersions, setSelectedVersions] = useState({});

  useEffect(() => {
    // accessTokenが無くてもAPIを呼ぶ
    getSoftwareCategories(tokens?.access)
      .then(res => {
        setCategories(res.data);
        const initialVersions = {};
        res.data.forEach(category => {
          category.files.forEach(file => {
            initialVersions[file.id] = file.versions[0]?.version || '';
          });
        });
        setSelectedVersions(initialVersions);
      })
      .catch(err => {
        console.error(err);
        setError('データの取得に失敗しました');
      });
  }, [tokens]);

  const handleVersionChange = (fileId, version) => {
    setSelectedVersions(prev => ({
      ...prev,
      [fileId]: version
    }));
  };

  const handleDownload = (file) => {
    const selectedVersion = selectedVersions[file.id];
    const versionData = file.versions.find(v => v.version === selectedVersion);
    if (versionData?.download_url) {
      window.open(versionData.download_url, '_blank');
    } else {
      alert('ダウンロードURLが見つかりません');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50">
      <DownloadHeader />
      
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <h1 className="text-5xl font-bold mb-4">ソフトウェアダウンロード</h1>
              <p className="text-xl text-blue-100">
                ファイルダウンロードリンクに問題がある場合お問い合わせください
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-red-50 border-l-4 border-red-500 p-4 mb-8"
          >
            <p className="text-red-700">{error}</p>
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-8">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">カテゴリー</h2>
              <nav className="space-y-2">
                {categories.map(category => {
                  const IconComponent = getCategoryIcon(category.name);
                  return (
                    <motion.button
                      key={category.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center space-x-3 ${
                        selectedCategory === category.id
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <IconComponent className="w-5 h-5" />
                      <span>{category.name}</span>
                    </motion.button>
                  );
                })}
              </nav>
            </div>
          </div>

          <div className="md:col-span-3">
            {categories.map(category => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mb-12 ${
                  selectedCategory && selectedCategory !== category.id ? 'hidden' : ''
                }`}
              >
                <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg p-6 mb-8">
                  <h2 className="text-3xl font-bold mb-2">{category.name}</h2>
                  <p className="text-blue-100">{category.description}</p>
                </div>

                {category.files.length === 0 ? (
                  <motion.div
                    whileHover={{ y: -4 }}
                    className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 p-8"
                  >
                    <div className="text-center">
                      <FiPackage className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-xl text-gray-500 font-medium">NO CONTENTS</p>
                      <p className="text-gray-400 mt-2">このカテゴリーにはまだコンテンツがありません</p>
                    </div>
                  </motion.div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
                    {category.files.map(file => (
                      <motion.div
                        key={file.id}
                        whileHover={{ y: -4 }}
                        className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 h-full flex flex-col"
                      >
                        <div className="p-6 flex flex-col flex-grow">
                          <h3 className="text-xl font-semibold mb-3 text-gray-800">{file.name}</h3>
                          <p className="text-gray-600 mb-6 flex-grow">{file.description}</p>

                          <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              バージョンを選択
                            </label>
                            <select
                              value={selectedVersions[file.id] || ''}
                              onChange={(e) => handleVersionChange(file.id, e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              {file.versions.map(ver => (
                                <option key={ver.version} value={ver.version}>
                                  v{ver.version}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="flex items-center space-x-2 text-gray-600">
                              <FiCalendar className="w-5 h-5 text-blue-500" />
                              <span>
                                {new Date(file.versions.find(v => v.version === selectedVersions[file.id])?.release_date).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-600">
                              <FiHardDrive className="w-5 h-5 text-cyan-500" />
                              <span>
                                {file.versions.find(v => v.version === selectedVersions[file.id])?.file_size}
                              </span>
                            </div>
                          </div>

                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleDownload(file)}
                            className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center space-x-2 transition-all shadow-md mt-auto"
                          >
                            <FiDownload className="w-5 h-5" />
                            <span>ダウンロード</span>
                          </motion.button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SoftwareDownloadPage;
